import { DeleteOutlined } from '@ant-design/icons';
import { Button, Form, Select } from 'antd';
import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useLazyGetClientServiceByIdQuery,
  useLazyGetClientServiceEmployeesQuery,
  useUpdateClientServiceEmployeesMutation,
} from '@app/api/ClientServices';
import { useLazyGetEmployeesListQuery } from '@app/api/Users';
import { DurationSelects } from '@app/components';
import {
  ERROR_REQUIRED_FIELD,
  RIGHTS_CLIENT_SERVICES,
  RIGHTS_IS_ATTACH,
} from '@app/constants';
import { useAccessRights, useUserPrint, useWindowSize } from '@app/hooks';
import { LeavePageContext, NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type { NAnalytics } from '@app/types';
import { transformMinutesToHours } from '@app/utils';

import type { ColumnsType } from 'antd/es/table';

interface DataType {
  Key: string;
  Name: string;
  Duration: string;
}

interface FormData {
  [key: string]: string | number;
}

const generateFullName = (
  allEmployees: NAnalytics.ModelEmployeeLite[],
  item: Record<string, string | number | null | boolean>,
  key: 'Id' | 'UserId'
): string => {
  const specificEmployee = allEmployees.find(({ Id }) => item[key] === Id);
  const firstName = specificEmployee?.NameFirst || '';
  const lastName = specificEmployee?.NameLast || '';
  const position = specificEmployee?.Position || '';
  const fullName = `${lastName} ${firstName}`.trim();

  return `${fullName}${position ? `, ${position}` : ''}`.trim();
};

const transformData = (inputData: FormData): DataType[] => {
  return Array.from({ length: Object.keys(inputData).length / 4 }, (_, i) => {
    const name = inputData[`Employee${i}`] as string;

    const hourMoment = moment(inputData[`Hour${i}`], 'HH');
    const minuteMoment = moment(inputData[`Minute${i}`], 'mm');

    const userId = inputData[`UserId${i}`];

    const duration = moment
      .duration({
        hours: hourMoment.hours(),
        minutes: minuteMoment.minutes(),
      })
      .asMinutes();

    return {
      Key: userId.toString(),
      Name: name,
      Duration: duration.toString(),
    };
  });
};

export const useClientsServiceEmployees = () => {
  const { hasUserRight: rightToAttachEmployees } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_ATTACH
  );
  const { activeBranchId } = useUserPrint();
  const [query] = useSearchParams();
  const [serviceId] = useState(Number(query.get('id')));
  const initialTab = Number(query.get('key'));
  const [allEmployees, setAllEmployees] = useState<
    NAnalytics.ModelEmployeeLite[]
  >([]);
  const [selectedEmployees, setSelectedEmployees] = useState<DataType[]>([]);
  const [unselectedEmployees, setUnselectedEmployees] = useState<
    NAnalytics.ModelEmployeeLite[]
  >([]);
  const [editableEmployeeTabData, setEditableEmployeeTabData] = useState<
    DataType[]
  >([]);
  const [isUpdateFormData, setIsUpdateFormData] = useState(false);
  const [loader, setLoader] = useState(false);
  const [form] = Form.useForm();
  const { showNotification } = useContext(NotificationContext);
  const { innerWidth } = useWindowSize();

  const { setNewData, prepareStartedData, setInitialData, newData } =
    useContext(LeavePageContext);

  const [getEmployeesList] = useLazyGetEmployeesListQuery();
  const [getClientServiceById] = useLazyGetClientServiceByIdQuery();
  const [getClientServiceEmployees] = useLazyGetClientServiceEmployeesQuery();
  const [updateClientServiceEmployees] =
    useUpdateClientServiceEmployeesMutation();

  useEffect(() => {
    const fetchEmployeesList = async () => {
      try {
        const data = await getEmployeesList({
          ActiveBranchId: activeBranchId,
        }).unwrap();

        const withoutDeleted = data.filter(({ IsDeleted }) => !IsDeleted);

        setAllEmployees(withoutDeleted);
        setUnselectedEmployees(withoutDeleted);
      } catch (error) {
        showNotification(handleCatchError(error), 'warning');
      }
    };

    fetchEmployeesList();
  }, [activeBranchId, getEmployeesList, showNotification]);

  useEffect(() => {
    if (initialTab === 2) {
      setInitialData(editableEmployeeTabData);
      setNewData(selectedEmployees);
    }
  }, [
    initialTab,
    editableEmployeeTabData,
    selectedEmployees,
    setInitialData,
    setNewData,
  ]);

  useEffect(() => {
    const fetchClientServiceEmployees = async () => {
      try {
        setLoader(true);
        const data = await getClientServiceEmployees({
          ActiveBranchId: activeBranchId,
          serviceId,
        }).unwrap();

        const withoutTerminatedAndDeleted = data.filter(({ UserId }) =>
          allEmployees.some(({ Id }) => Id === UserId)
        );

        const handledData = withoutTerminatedAndDeleted.map((item) => ({
          Key: String(item.UserId),
          Name: generateFullName(allEmployees, { ...item }, 'UserId'),
          Duration: String(item.Duration),
        }));

        setSelectedEmployees(handledData);
        prepareStartedData(handledData);
        setEditableEmployeeTabData(handledData);

        const _unselectedEmployees = allEmployees.filter(
          (item) => !handledData.some((_item) => Number(_item.Key) === item.Id)
        );
        setUnselectedEmployees(_unselectedEmployees);

        withoutTerminatedAndDeleted.forEach((item, index) => {
          form.setFieldsValue({
            [`UserId${index}`]: item.UserId,
            [`Employee${index}`]: generateFullName(
              allEmployees,
              { ...item },
              'UserId'
            ),
            [`Hour${index}`]: `${
              transformMinutesToHours(item.Duration).hours
            } ч`,
            [`Minute${index}`]: `${
              transformMinutesToHours(item.Duration).minutes
            } мин`,
          });
        });
      } catch (error) {
        showNotification(error, 'warning');
      } finally {
        setLoader(false);
      }
    };

    if (serviceId && allEmployees.length > 0) {
      fetchClientServiceEmployees();
    } else {
      prepareStartedData([]);
      setEditableEmployeeTabData([]);
    }
  }, [
    serviceId,
    allEmployees,
    form,
    prepareStartedData,
    showNotification,
    activeBranchId,
    getClientServiceEmployees,
  ]);

  useEffect(() => {
    const updatedFields: Record<string, string> = {};
    selectedEmployees.forEach((data, idx) => {
      updatedFields[`Employee${idx}`] = data.Name;
      updatedFields[`Hour${idx}`] = `${Math.floor(+data.Duration / 60)} ч`;
      updatedFields[`Minute${idx}`] = `${+data.Duration % 60} мин`;
      updatedFields[`UserId${idx}`] = data.Key;
    });
    form.setFieldsValue(updatedFields);
  }, [isUpdateFormData, form, selectedEmployees]);

  const memoizedUnselectedEmployees = useMemo(
    () =>
      unselectedEmployees.map((item) => ({
        value: item.Id,
        label: generateSpanWithDataTestId(
          generateFullName(allEmployees, item, 'Id'),
          'executor-item'
        ),
        name: generateFullName(allEmployees, item, 'Id'),
      })),
    [unselectedEmployees, allEmployees]
  );

  const defineDisabling = () => !rightToAttachEmployees;

  const columns: ColumnsType<DataType> = [
    {
      title: generateSpanWithDataTestId('Исполнитель', 'executor'),
      dataIndex: 'Name',
      key: 'Name',
      render: (value, record, index) => (
        <Form.Item
          key={`Employee${index}`}
          name={`Employee${index}`}
          rules={[{ required: true, message: ERROR_REQUIRED_FIELD }]}
        >
          <Select
            data-testid="executors-select"
            style={{ width: innerWidth > 1185 ? 290 : '100%' }}
            popupMatchSelectWidth={false}
            size="large"
            placeholder="Не выбрано"
            disabled={defineDisabling()}
            options={memoizedUnselectedEmployees}
            onSelect={(value, option) => {
              const user = allEmployees.find(({ Id }) => value === Number(Id));
              const userId = user?.Id;

              form.setFieldValue(`UserId${index}`, userId);
              form.setFieldValue(`Employee${index}`, option.name);

              const formData = transformData(form.getFieldsValue());

              setSelectedEmployees(formData);

              const filteredUnselectedEmployees = allEmployees.filter(
                (item) =>
                  !formData.some((_item) => Number(_item.Key) === item.Id)
              );

              setUnselectedEmployees(filteredUnselectedEmployees);
            }}
          />
        </Form.Item>
      ),
      width: innerWidth > 1185 ? '40%' : 'auto',
    },
    {
      title: generateSpanWithDataTestId('Длительность', 'duration'),
      dataIndex: 'Duration',
      key: 'Duration',
      render: (value, record, index) => (
        <DurationSelects
          key={`Duration${index}`}
          onHourSelect={(value) => {
            form.setFieldValue(`Hour${index}`, `${value} ч`);
          }}
          onMinuteSelect={(value) => {
            form.setFieldValue(`Minute${index}`, `${value} мин`);
          }}
          index={index}
        />
      ),
      width: '50%',
    },

    {
      title: '',
      key: 'Action',
      render: (text: string, record: DataType, index: number) => (
        <Form.Item key={`Action${index}`} name={`UserId${index}`}>
          <Button
            data-testid="delete-executor"
            danger
            size="large"
            disabled={defineDisabling()}
            icon={<DeleteOutlined />}
            onClick={() => {
              setIsUpdateFormData((prev) => !prev);
              const foundEmployee = allEmployees.find(
                (item) => item.Id === +record.Key
              );

              if (foundEmployee) {
                setUnselectedEmployees((prev) => [...prev, foundEmployee]);
              }

              const filteredEmployees = selectedEmployees.filter(
                (item) => item.Key !== record.Key
              );

              setSelectedEmployees(filteredEmployees);
              setNewData((data: DataType[]) =>
                data.filter((item) => item.Key !== record.Key)
              );
            }}
          />
        </Form.Item>
      ),
      width: '10%',
    },
  ];

  const sendDataToDb = async () => {
    setLoader(true);
    try {
      const data = form.getFieldsValue();

      const keysPerOperation = 4;
      const operationsCount = Object.keys(data).length / keysPerOperation;

      const transformDuration = (hours: string, minutes: string) => {
        const handledHours = Number(hours.replace(' ч', ''));
        const handledMinutes = Number(minutes.replace(' мин', ''));
        return handledHours * 60 + handledMinutes;
      };

      const adaptedDataForServer = Array.from(
        { length: operationsCount },
        (_, index: number) => ({
          Id: 0,
          UserId: data[`UserId${index}`],
          ServiceId: serviceId,
          Duration: transformDuration(
            data[`Hour${index}`],
            data[`Minute${index}`]
          ),
        })
      );

      await updateClientServiceEmployees({
        ActiveBranchId: activeBranchId,
        serviceId,
        data: adaptedDataForServer,
      }).unwrap();

      showNotification('Данные сохранены успешно', 'success');
      setInitialData(newData);
      setEditableEmployeeTabData(newData);
    } catch (error) {
      showNotification(error, 'warning');
    } finally {
      setLoader(false);
    }
  };

  const addEmployee = async () => {
    if (!activeBranchId || !serviceId) return;
    try {
      const { Duration } = await getClientServiceById({
        ActiveBranchId: activeBranchId,
        serviceId,
      }).unwrap();

      const newData: DataType = {
        Key: String(Math.floor(Math.random() * 10000)),
        Name: '',
        Duration: String(Duration),
      };
      const { hours, minutes } = transformMinutesToHours(Duration);

      setSelectedEmployees((oldData) => [...oldData, newData]);
      setNewData((oldData: DataType[]) => [...oldData, newData]);

      const selectsCount = Object.keys(form.getFieldsValue()).length / 4;

      form.setFieldValue([`Employee${selectsCount}`], '');
      form.setFieldValue([`Hour${selectsCount}`], `${hours} ч`);
      form.setFieldValue([`Minute${selectsCount}`], `${minutes} мин`);
      form.setFieldValue([`UserId${selectsCount}`], newData.Key);
    } catch (error) {
      showNotification(error, 'warning');
    }
  };

  return {
    columns,
    data: selectedEmployees,
    allEmployees,
    loader,
    form,
    sendDataToDb,
    addEmployee,
    unselectedEmployees,
  };
};
